import React, { useState } from 'react';
import { ImagePreview, ImageUpload } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { Vehicle, vehiclesApi } from '@/services/api/vehicles';
import { extractApiErrorMessage, getUserEmail } from '@/utils';

interface VehiclePhotoCardProps {
  vehicle: Vehicle;
  /** Refetches the detail so the card renders the stored (main-bucket) URL. */
  onChanged: () => void;
}

/**
 * The unit's photo on its detail screen: what is on file, and the way to put a
 * new one there. Until now the photo could only be set while creating the unit
 * or receiving it, so a blurred or missing shot meant editing the whole vehicle.
 */
const VehiclePhotoCard: React.FC<VehiclePhotoCardProps> = ({
  vehicle,
  onChanged,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // Set while a replacement is being picked, so the card shows the upload
  // control instead of the photo it is about to replace.
  const [isReplacing, setIsReplacing] = useState(false);

  const save = async (vehicleImageURL: string) => {
    if (!vehicle.ID) return;
    setIsSaving(true);
    try {
      // PATCH /v1/vehicles/:id validates the unit's identity fields even on a
      // partial edit, so they travel unchanged alongside the photo. Everything
      // omitted is left as it is.
      await vehiclesApi.updateVehicle(vehicle.ID, {
        platNomor: vehicle.platNomor,
        vin: vehicle.vin,
        modelID: vehicle.modelID,
        color: vehicle.color,
        vehicleImageURL,
        updatedBy: getUserEmail(),
      });
      showSuccessToast(
        'Success',
        vehicleImageURL ? 'Foto unit diperbarui' : 'Foto unit dihapus'
      );
      setIsReplacing(false);
      onChanged();
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Gagal menyimpan foto unit')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const storedPhoto = vehicle.vehicleImageURL || '';
  const showUpload = isReplacing || !storedPhoto;

  return (
    <div className="border border-gray-200 shadow-sm rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-5 pt-4 pb-2">
        <div className="text-[16px] font-medium text-gray-800">Unit Photo</div>
        {storedPhoto && !showUpload && (
          <button
            type="button"
            onClick={() => setIsReplacing(true)}
            disabled={isSaving}
            className="text-xs font-semibold text-[#5E2AAC] hover:underline disabled:text-gray-400"
          >
            Ganti foto
          </button>
        )}
      </div>

      <div className="px-5 py-4">
        {showUpload ? (
          <>
            <ImageUpload
              value=""
              onUploaded={save}
              onRemove={() => setIsReplacing(false)}
              imageType="vehicle"
              title={
                storedPhoto ? 'Pilih foto pengganti' : 'Tambahkan foto unit'
              }
              onUploadingChange={setIsUploading}
              disabled={isSaving}
            />
            <div className="mt-2 flex items-center gap-3">
              {(isUploading || isSaving) && (
                <span className="text-xs text-gray-500">
                  {isSaving ? 'Menyimpan…' : 'Mengunggah…'}
                </span>
              )}
              {storedPhoto && !isUploading && !isSaving && (
                <button
                  type="button"
                  onClick={() => setIsReplacing(false)}
                  className="text-xs font-semibold text-gray-500 hover:underline"
                >
                  Batal
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            <ImagePreview
              src={storedPhoto}
              alt={`Foto unit ${vehicle.platNomor || ''}`.trim()}
              className="w-full max-h-64 rounded-md object-cover border border-gray-200"
            />
            <button
              type="button"
              onClick={() => save('')}
              disabled={isSaving}
              className="mt-3 text-xs font-semibold text-red-500 hover:underline disabled:text-gray-400"
            >
              Hapus foto
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default VehiclePhotoCard;
